import { FilterLevel, FilterVM } from '../../../common/view-model';

interface BuildCategorySelectionPlanParams {
	filter: FilterVM;
	isCheck: boolean;
	selectedFilters: FilterVM[];
	parentLength?: number;
	parentName?: string;
}

export type CategorySelectionAction =
	| { type: 'add'; filter: FilterVM }
	| { type: 'remove'; filter: FilterVM };

const buildL2SelectionPlan = (
	filter: FilterVM,
	isCheck: boolean,
	selectedFilters: FilterVM[]
): CategorySelectionAction[] => {
	if (isCheck) {
		const actions: CategorySelectionAction[] = [
			{
				type: 'add',
				filter: {
					...filter,
					children: [],
					isAutoSelected: false
				}
			}
		];

		(filter.children ?? []).forEach((child: FilterVM) => {
			actions.push({
				type: 'add',
				filter: {
					...child,
					parentId: filter.id,
					level: FilterLevel.THIRD,
					isChecked: true,
					isAutoSelected: false
				}
			});
		});

		const manualL1Selection = selectedFilters.find(
			item =>
				item.id === filter.parentId && item.level === FilterLevel.FIRST && !item.isAutoSelected
		);

		if (manualL1Selection) {
			actions.push({
				type: 'remove',
				filter: manualL1Selection
			});
		}

		return actions;
	}

	return [
		{
			type: 'remove',
			filter: {
				...filter,
				children: [],
				isAutoSelected: false
			}
		},
		...selectedFilters
			.filter(item => item.parentId === filter.id && item.level === FilterLevel.THIRD)
			.map(child => ({
				type: 'remove' as const,
				filter: child
			}))
	];
};

const buildL3SelectionPlan = (
	filter: FilterVM,
	isCheck: boolean,
	selectedFilters: FilterVM[],
	parentLength: number,
	parentName?: string
): CategorySelectionAction[] => {
	const plan: CategorySelectionAction[] = [];
	const parentId = filter.parentId;

	const manualParentSelection = selectedFilters.find(
		item => item.id === parentId && !item.isAutoSelected
	);

	if (isCheck) {
		plan.push({
			type: 'add',
			filter: {
				...filter,
				isAutoSelected: false
			}
		});

		const selectedChildren = selectedFilters.filter(
			item => item.parentId === parentId && item.level === FilterLevel.THIRD
		).length;

		const shouldAutoSelectParent =
			parentId && parentLength > 0 && selectedChildren + 1 >= parentLength;

		if (shouldAutoSelectParent) {
			plan.push({
				type: 'add',
				filter: {
					id: parentId,
					name: parentName ?? filter.name,
					level: FilterLevel.SECOND,
					parentId: undefined,
					isAutoSelected: true,
					isChecked: true,
					children: [],
					type: filter.type
				}
			});
		} else if (manualParentSelection) {
			plan.push({
				type: 'remove',
				filter: manualParentSelection
			});

			if (manualParentSelection.parentId) {
				const manualL1Selection = selectedFilters.find(
					item =>
						item.id === manualParentSelection.parentId &&
						item.level === FilterLevel.FIRST &&
						!item.isAutoSelected
				);

				if (manualL1Selection) {
					plan.push({
						type: 'remove',
						filter: manualL1Selection
					});
				}
			}
		}

		return plan;
	}

	plan.push({
		type: 'remove',
		filter: {
			...filter,
			isAutoSelected: false
		}
	});

	const selectedParent = selectedFilters.find(
		item => item.id === parentId && item.level === FilterLevel.SECOND
	);

	if (selectedParent) {
		plan.push({
			type: 'remove',
			filter: selectedParent
		});
	}

	return plan;
};

export const buildCategorySelectionPlan = ({
	filter,
	isCheck,
	selectedFilters,
	parentLength = 0,
	parentName
}: BuildCategorySelectionPlanParams): CategorySelectionAction[] => {
	if (filter.level === FilterLevel.SECOND) {
		return buildL2SelectionPlan(filter, isCheck, selectedFilters);
	}

	if (filter.level === FilterLevel.THIRD) {
		return buildL3SelectionPlan(filter, isCheck, selectedFilters, parentLength, parentName);
	}

	return [];
};
